/**
 * Timezone utilities — all timestamps shown in the app should pass through
 * `formatTz()` / `formatTzDate()` so the viewer always sees their local time.
 *
 * The viewer's timezone comes from user.timezone (set in their profile). If
 * the user hasn't set one, we fall back to the browser's Intl tz.
 */
/**
 * Normalise a backend-emitted timestamp.
 *
 * The Pulse API emits naive ISO strings (e.g. "2026-04-22T23:13:12.870546")
 * because MS SQL DATETIME2 columns don't carry a tz offset. The column
 * values are *written as UTC* (`SYSUTCDATETIME()`), but JavaScript's
 * `new Date(naive)` parses them as LOCAL time — which produces the wrong
 * clock face everywhere downstream.
 *
 * Rule: if the string has no 'Z' and no explicit ±HH:MM offset, treat it
 * as UTC by appending 'Z'. Anything already annotated (ISO with offset,
 * Date instance, millis) is returned untouched.
 */
export function asUtc(value) {
  if (value == null || value === "") return value;
  if (value instanceof Date) return value;
  if (typeof value !== "string") return value;
  // Already has offset/Z anywhere after the 'T' → leave alone.
  if (/[Zz]|[+-]\d{2}:?\d{2}$/.test(value)) return value;
  return value + "Z";
}

export const BROWSER_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch { return "UTC"; }
})();

/**
 * Read the signed-in user's timezone directly from localStorage so every
 * component can format timestamps without threading `tz` through props.
 * Falls back to the browser's tz when no user or no `timezone` set.
 */
export function userTz() {
  try {
    const raw = localStorage.getItem("eyvo_user");
    if (!raw) return BROWSER_TZ;
    const u = JSON.parse(raw);
    return u?.timezone || BROWSER_TZ;
  } catch { return BROWSER_TZ; }
}

export function resolveTz(user) {
  return user?.timezone || BROWSER_TZ;
}

/**
 * One-stop "format in the current user's timezone" helper — no args beyond
 * the value needed. Use this everywhere backend timestamps show up.
 */
export function formatLocal(value, opts = {}) { return formatTz(value, userTz(), opts); }
export function formatLocalDate(value) { return formatTzDate(value, userTz()); }
export function formatLocalTime(value) { return formatTzTime(value, userTz()); }

/** Format an ISO/Date value as "Apr 17, 2026, 5:11:30 PM IST". */
export function formatTz(value, tz, opts = {}) {
  if (!value) return "";
  const raw = value instanceof Date ? value : new Date(asUtc(value));
  if (isNaN(raw.getTime())) return "";
  const targetTz = tz || BROWSER_TZ;
  // Build the date/time portion ourselves (without the built-in tz name)
  // and append a clean abbreviation like "IST" / "PDT" / "EST" derived
  // from the long timezone name. Browsers' built-in `timeZoneName: "short"`
  // returns ugly "GMT+5:30" for many zones — this fixes that.
  const includeZone = opts.withZoneName !== false;
  const dtOpts = {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
    timeZone: targetTz,
    hour12: opts.hour12 !== false,
    ...opts,
  };
  delete dtOpts.withZoneName;
  try {
    const formatted = new Intl.DateTimeFormat(undefined, dtOpts).format(raw);
    if (!includeZone) return formatted;
    const abbrev = tzAbbrev(targetTz, raw);
    return abbrev ? `${formatted} ${abbrev}` : formatted;
  } catch { return raw.toLocaleString(); }
}

/**
 * Best-effort short tz abbreviation derived from the LONG timezone name
 * (e.g. "India Standard Time" → "IST", "Pacific Daylight Time" → "PDT").
 * Falls back to the IANA city when the browser doesn't have a long name.
 */
export function tzAbbrev(tz, when) {
  try {
    // Curated overrides for IANA zones whose Intl long name doesn't
    // acronym to the canonical abbreviation users expect.
    const OVERRIDES = { UTC: "UTC", "Etc/UTC": "UTC", "Etc/GMT": "GMT" };
    if (OVERRIDES[tz]) return OVERRIDES[tz];
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, timeZoneName: "long",
    }).formatToParts(when || new Date());
    const longName = (parts.find(p => p.type === "timeZoneName") || {}).value || "";
    if (!longName) return "";
    // Acronym from capital letters in the long name. Works cleanly for
    // "India Standard Time" → IST, "Pacific Daylight Time" → PDT,
    // "Eastern Standard Time" → EST, "Greenwich Mean Time" → GMT.
    const acronym = (longName.match(/\b[A-Z]/g) || []).join("");
    if (acronym && acronym.length >= 2) return acronym;
    // Long name had no acronymable structure — fall back to the
    // city portion of the IANA id (e.g. "Asia/Kolkata" → "Kolkata").
    const last = (tz || "").split("/").pop() || "";
    return last.replace(/_/g, " ");
  } catch {
    return "";
  }
}

/** Short date only, like "Apr 17, 2026" in the user's tz. */
export function formatTzDate(value, tz) {
  if (!value) return "";
  const raw = value instanceof Date ? value : new Date(asUtc(value));
  if (isNaN(raw.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      timeZone: tz || BROWSER_TZ,
    }).format(raw);
  } catch { return raw.toLocaleDateString(); }
}

/** HH:MM only, for compact secondary labels. */
export function formatTzTime(value, tz) {
  if (!value) return "";
  const raw = value instanceof Date ? value : new Date(asUtc(value));
  if (isNaN(raw.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit", minute: "2-digit", timeZone: tz || BROWSER_TZ, hour12: true,
    }).format(raw);
  } catch { return raw.toLocaleTimeString(); }
}

/** A curated list of IANA timezones shown in the profile picker. */
export const TIMEZONE_OPTIONS = [
  { value: "Pacific/Honolulu", label: "(GMT-10) Hawaii — Honolulu" },
  { value: "America/Anchorage", label: "(GMT-9/-8) Alaska — Anchorage" },
  { value: "America/Los_Angeles", label: "(GMT-8/-7) Pacific — Los Angeles" },
  { value: "America/Phoenix", label: "(GMT-7) Mountain (no DST) — Phoenix" },
  { value: "America/Denver", label: "(GMT-7/-6) Mountain — Denver" },
  { value: "America/Chicago", label: "(GMT-6/-5) Central — Chicago" },
  { value: "America/New_York", label: "(GMT-5/-4) Eastern — New York" },
  { value: "America/Toronto", label: "(GMT-5/-4) Eastern — Toronto" },
  { value: "America/Mexico_City", label: "(GMT-6) Mexico City" },
  { value: "America/Sao_Paulo", label: "(GMT-3) São Paulo" },
  { value: "America/Argentina/Buenos_Aires", label: "(GMT-3) Buenos Aires" },
  { value: "UTC", label: "(GMT+0) UTC" },
  { value: "Europe/London", label: "(GMT+0/+1) London" },
  { value: "Europe/Dublin", label: "(GMT+0/+1) Dublin" },
  { value: "Europe/Lisbon", label: "(GMT+0/+1) Lisbon" },
  { value: "Europe/Paris", label: "(GMT+1/+2) Paris" },
  { value: "Europe/Berlin", label: "(GMT+1/+2) Berlin" },
  { value: "Europe/Madrid", label: "(GMT+1/+2) Madrid" },
  { value: "Europe/Rome", label: "(GMT+1/+2) Rome" },
  { value: "Europe/Amsterdam", label: "(GMT+1/+2) Amsterdam" },
  { value: "Europe/Stockholm", label: "(GMT+1/+2) Stockholm" },
  { value: "Europe/Warsaw", label: "(GMT+1/+2) Warsaw" },
  { value: "Europe/Athens", label: "(GMT+2/+3) Athens" },
  { value: "Africa/Cairo", label: "(GMT+2) Cairo" },
  { value: "Africa/Johannesburg", label: "(GMT+2) Johannesburg" },
  { value: "Europe/Istanbul", label: "(GMT+3) Istanbul" },
  { value: "Asia/Jerusalem", label: "(GMT+2/+3) Jerusalem" },
  { value: "Asia/Dubai", label: "(GMT+4) Dubai" },
  { value: "Asia/Karachi", label: "(GMT+5) Karachi" },
  { value: "Asia/Kolkata", label: "(GMT+5:30) Mumbai · New Delhi · Kolkata" },
  { value: "Asia/Dhaka", label: "(GMT+6) Dhaka" },
  { value: "Asia/Bangkok", label: "(GMT+7) Bangkok · Jakarta" },
  { value: "Asia/Singapore", label: "(GMT+8) Singapore · Kuala Lumpur" },
  { value: "Asia/Hong_Kong", label: "(GMT+8) Hong Kong" },
  { value: "Asia/Shanghai", label: "(GMT+8) Shanghai · Beijing" },
  { value: "Asia/Taipei", label: "(GMT+8) Taipei" },
  { value: "Asia/Manila", label: "(GMT+8) Manila" },
  { value: "Asia/Tokyo", label: "(GMT+9) Tokyo" },
  { value: "Asia/Seoul", label: "(GMT+9) Seoul" },
  { value: "Australia/Perth", label: "(GMT+8) Perth" },
  { value: "Australia/Adelaide", label: "(GMT+9:30/+10:30) Adelaide" },
  { value: "Australia/Sydney", label: "(GMT+10/+11) Sydney · Melbourne" },
  { value: "Pacific/Auckland", label: "(GMT+12/+13) Auckland" },
];

// Session 68 pt4 — pragmatic country → IANA timezone fallback so the
// ClientOverviewPage timezone field has a sensible default when an
// operator hasn't explicitly chosen one. Maps to the most populated
// timezone for each country/region; multi-zone countries (US, CA,
// AU, BR, RU) fall back to the central/east one. Not exhaustive —
// the SPA still lets the operator override via dropdown.
const COUNTRY_TZ = {
  "US": "America/New_York",
  "USA": "America/New_York",
  "United States": "America/New_York",
  "United States of America": "America/New_York",
  "GB": "Europe/London",
  "UK": "Europe/London",
  "United Kingdom": "Europe/London",
  "England": "Europe/London",
  "Scotland": "Europe/London",
  "Wales": "Europe/London",
  "Ireland": "Europe/Dublin",
  "Canada": "America/Toronto",
  "CA": "America/Toronto",
  "Australia": "Australia/Sydney",
  "AU": "Australia/Sydney",
  "New Zealand": "Pacific/Auckland",
  "NZ": "Pacific/Auckland",
  "France": "Europe/Paris",
  "FR": "Europe/Paris",
  "Germany": "Europe/Berlin",
  "DE": "Europe/Berlin",
  "Spain": "Europe/Madrid",
  "ES": "Europe/Madrid",
  "Italy": "Europe/Rome",
  "IT": "Europe/Rome",
  "Netherlands": "Europe/Amsterdam",
  "NL": "Europe/Amsterdam",
  "Belgium": "Europe/Brussels",
  "Switzerland": "Europe/Zurich",
  "Sweden": "Europe/Stockholm",
  "Norway": "Europe/Oslo",
  "Denmark": "Europe/Copenhagen",
  "Finland": "Europe/Helsinki",
  "Poland": "Europe/Warsaw",
  "Portugal": "Europe/Lisbon",
  "Greece": "Europe/Athens",
  "Czechia": "Europe/Prague",
  "Czech Republic": "Europe/Prague",
  "Austria": "Europe/Vienna",
  "Russia": "Europe/Moscow",
  "RU": "Europe/Moscow",
  "Israel": "Asia/Jerusalem",
  "IL": "Asia/Jerusalem",
  "UAE": "Asia/Dubai",
  "United Arab Emirates": "Asia/Dubai",
  "India": "Asia/Kolkata",
  "IN": "Asia/Kolkata",
  "Pakistan": "Asia/Karachi",
  "Singapore": "Asia/Singapore",
  "SG": "Asia/Singapore",
  "Hong Kong": "Asia/Hong_Kong",
  "China": "Asia/Shanghai",
  "CN": "Asia/Shanghai",
  "Japan": "Asia/Tokyo",
  "JP": "Asia/Tokyo",
  "South Korea": "Asia/Seoul",
  "Korea": "Asia/Seoul",
  "Mexico": "America/Mexico_City",
  "Brazil": "America/Sao_Paulo",
  "BR": "America/Sao_Paulo",
  "Argentina": "America/Argentina/Buenos_Aires",
  "Chile": "America/Santiago",
  "South Africa": "Africa/Johannesburg",
  "ZA": "Africa/Johannesburg",
  "Egypt": "Africa/Cairo",
};

// US state → tz override. Covers the 4 mainland zones cleanly,
// plus AK/HI. Falls through to America/New_York when the state
// is empty or unmapped (Eastern is the largest population zone).
const US_STATE_TZ = {
  "CA": "America/Los_Angeles", "California": "America/Los_Angeles",
  "WA": "America/Los_Angeles", "Washington": "America/Los_Angeles",
  "OR": "America/Los_Angeles", "Oregon": "America/Los_Angeles",
  "NV": "America/Los_Angeles", "Nevada": "America/Los_Angeles",
  "AZ": "America/Phoenix",     "Arizona": "America/Phoenix",
  "CO": "America/Denver",      "Colorado": "America/Denver",
  "MT": "America/Denver",      "Montana": "America/Denver",
  "ID": "America/Denver",      "Idaho": "America/Denver",
  "UT": "America/Denver",      "Utah": "America/Denver",
  "NM": "America/Denver",      "New Mexico": "America/Denver",
  "WY": "America/Denver",      "Wyoming": "America/Denver",
  "TX": "America/Chicago",     "Texas": "America/Chicago",
  "IL": "America/Chicago",     "Illinois": "America/Chicago",
  "MN": "America/Chicago",     "Minnesota": "America/Chicago",
  "WI": "America/Chicago",     "Wisconsin": "America/Chicago",
  "IA": "America/Chicago",     "Iowa": "America/Chicago",
  "MO": "America/Chicago",     "Missouri": "America/Chicago",
  "AR": "America/Chicago",     "Arkansas": "America/Chicago",
  "LA": "America/Chicago",     "Louisiana": "America/Chicago",
  "OK": "America/Chicago",     "Oklahoma": "America/Chicago",
  "KS": "America/Chicago",     "Kansas": "America/Chicago",
  "NE": "America/Chicago",     "Nebraska": "America/Chicago",
  "ND": "America/Chicago",     "North Dakota": "America/Chicago",
  "SD": "America/Chicago",     "South Dakota": "America/Chicago",
  "AL": "America/Chicago",     "Alabama": "America/Chicago",
  "MS": "America/Chicago",     "Mississippi": "America/Chicago",
  "TN": "America/Chicago",     "Tennessee": "America/Chicago",
  "AK": "America/Anchorage",   "Alaska": "America/Anchorage",
  "HI": "Pacific/Honolulu",    "Hawaii": "Pacific/Honolulu",
};

/**
 * inferTimezone({ country, state_region })
 * Returns the best-guess IANA timezone for a country/state combo,
 * or "" when nothing matches. Cheap synchronous lookup — safe to
 * call inline in render. Used by the ClientOverviewPage as the
 * default when no explicit timezone has been saved.
 */
export function inferTimezone({ country, state_region } = {}) {
  const c = (country || "").trim();
  const s = (state_region || "").trim();
  // US — favour the state-level mapping when present (Mike's main
  // ask: Pacific should be the default for California, Mountain for
  // Colorado, etc., not whatever the country-level fallback sets).
  if (/^(us|usa|united states.*)$/i.test(c) && s) {
    const hit = US_STATE_TZ[s] || US_STATE_TZ[s.toUpperCase()];
    if (hit) return hit;
  }
  if (!c) return "";
  return COUNTRY_TZ[c] || COUNTRY_TZ[c.toUpperCase()] || "";
}
